import type { CanvasSectionBlockLayout } from '../sectionRegistry';
import type {
  ActiveAlignmentGuide,
  AlignmentGuide,
  AlignmentGuideAxis,
  AlignmentGuideKind,
  AlignmentGuides,
  AlignmentLineGuide,
  BoxAnchor,
  GapGuide,
  GapGuideDebug,
  SectionAnchor,
  SpacingGuide,
  TargetAnchor,
} from './types';

export type AlignmentLayoutBlock = {
  id: string;
  layout: CanvasSectionBlockLayout;
  leftPx?: number;
  topPx?: number;
  offsetXPx?: number;
  offsetYPx?: number;
  widthPx?: number;
  heightPx?: number;
};

export type AlignmentBox = {
  id?: string;
  left: number;
  top: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
  centerX: number;
  centerY: number;
};

export function createAlignmentBoxFromLayout(
  layout: CanvasSectionBlockLayout,
  colWidth: number,
  rowHeight: number,
  options?: {
    id?: string;
    leftPx?: number;
    topPx?: number;
    offsetXPx?: number;
    offsetYPx?: number;
    widthPx?: number;
    heightPx?: number;
  }
): AlignmentBox {
  const left =
    typeof options?.leftPx === 'number'
      ? options.leftPx
      : (layout.x - 1) * colWidth + (options?.offsetXPx ?? 0);
  const top =
    typeof options?.topPx === 'number'
      ? options.topPx
      : (layout.y - 1) * rowHeight + (options?.offsetYPx ?? 0);
  const width = Math.max(1, options?.widthPx ?? layout.w * colWidth);
  const height = Math.max(1, options?.heightPx ?? layout.h * rowHeight);
  const right = left + width;
  const bottom = top + height;

  return {
    id: options?.id,
    left,
    top,
    width,
    height,
    right,
    bottom,
    centerX: left + width / 2,
    centerY: top + height / 2,
  };
}

export function createAlignmentBoxesFromLayouts(
  blocks: AlignmentLayoutBlock[],
  colWidth: number,
  rowHeight: number
): AlignmentBox[] {
  return blocks.map((block) =>
    createAlignmentBoxFromLayout(block.layout, colWidth, rowHeight, {
      id: block.id,
      leftPx: block.leftPx,
      topPx: block.topPx,
      offsetXPx: block.offsetXPx,
      offsetYPx: block.offsetYPx,
      widthPx: block.widthPx,
      heightPx: block.heightPx,
    })
  );
}

export function translateAlignmentBox(box: AlignmentBox, deltaX: number, deltaY: number): AlignmentBox {
  return {
    ...box,
    left: box.left + deltaX,
    right: box.right + deltaX,
    centerX: box.centerX + deltaX,
    top: box.top + deltaY,
    bottom: box.bottom + deltaY,
    centerY: box.centerY + deltaY,
  };
}

export function resizeAlignmentBox(box: AlignmentBox, width: number, height: number): AlignmentBox {
  const resolvedWidth = Math.max(1, width);
  const resolvedHeight = Math.max(1, height);
  if (
    Math.abs(box.width - resolvedWidth) <= 0.5 &&
    Math.abs(box.height - resolvedHeight) <= 0.5
  ) {
    return box;
  }
  const right = box.left + resolvedWidth;
  const bottom = box.top + resolvedHeight;
  return {
    ...box,
    width: resolvedWidth,
    height: resolvedHeight,
    right,
    bottom,
    centerX: box.left + resolvedWidth / 2,
    centerY: box.top + resolvedHeight / 2,
  };
}

const ROW_COLUMN_TOLERANCE_PX = 14;
const LARGE_CONTAINER_WIDTH_RATIO = 0.65;
const SIMILAR_SIZE_MIN_RATIO = 0.2;
const SIMILAR_SIZE_MAX_RATIO = 2.4;
const CONTAIN_TOLERANCE_PX = 2;
const GAP_TOUCH_TOLERANCE_PX = 1;
const OVERLAP_DISPLAY_THRESHOLD_PX = 4;
const GAP_TICK_HALF_HEIGHT_PX = 6;

const ROW_HORIZONTAL_RHYTHM_BLOCK_IDS = new Set([
  'holding-instagram',
  'holding-email',
  'holding-facebook',
  'snap-fixture-row-a-1',
  'snap-fixture-drag-target',
  'snap-fixture-row-a-3',
]);

export type CandidateRejectionReason =
  | 'large-container'
  | 'not-same-row'
  | 'not-same-column'
  | 'contains-moving-box'
  | 'not-immediate-neighbour'
  | 'lower-priority-kind'
  | 'not-row-peer'
  | 'not-column-peer';

export type CandidateRejection = {
  blockId: string;
  reason: CandidateRejectionReason;
};

export type AlignmentBoxSource = 'content' | 'frame' | 'text-paragraph' | 'layout';

export type VerticalAlignmentYPriority =
  | 'row-peer'
  | 'same-column-similar'
  | 'same-column'
  | 'same-row'
  | 'nearby-content'
  | 'section-anchor';

export type VerticalAlignmentCandidateDebug = {
  blockId: string;
  blockKind?: string;
  chosenBoxSource?: AlignmentBoxSource;
  activeVisualBox?: ReturnType<typeof serializeAlignmentBox>;
  testedAnchors: Array<'top' | 'centerY' | 'bottom'>;
  distancesPx: {
    topToTop?: number;
    centerYToCenterY?: number;
    bottomToBottom?: number;
  };
  acceptedForY: boolean;
  rejectionReason?: string;
  priority?: VerticalAlignmentYPriority | 'excluded';
};

export type VerticalSpacingDebug = {
  movingBlockId?: string;
  movingBox: ReturnType<typeof serializeAlignmentBox>;
  stackPeerIds: string[];
  topNeighbour?: {
    blockId?: string;
    gapPx: number;
    overlap: boolean;
    chosenBoxSource: AlignmentBoxSource;
  };
  bottomNeighbour?: {
    blockId?: string;
    gapPx: number;
    overlap: boolean;
    chosenBoxSource: AlignmentBoxSource;
  };
  equalSpacing?: {
    topGapPx: number;
    bottomGapPx: number;
    differencePx: number;
    withinTolerance: boolean;
    rendered: boolean;
  };
  rejectedCandidates: Array<{ blockId: string; reason: string }>;
};

export type VerticalAlignmentDebug = {
  movingBlockId?: string;
  movingBoxSource: AlignmentBoxSource;
  movingBox: ReturnType<typeof serializeAlignmentBox>;
  candidates: VerticalAlignmentCandidateDebug[];
  activeYGuide?: {
    movingAnchor: BoxAnchor;
    targetBlockId?: string;
    targetAnchor: TargetAnchor;
    distancePx: number;
    snapDeltaPx: number;
    priority?: VerticalAlignmentYPriority | 'section-anchor';
    rendered: boolean;
  };
  suppressedYGuides?: Array<{ targetBlockId?: string; reason: string }>;
};

export type AlignmentAnalysis = {
  movingBox: AlignmentBox;
  candidateCount: number;
  candidateRejections?: CandidateRejection[];
  rowPeerIds?: string[];
  explicitRowMode?: boolean;
  explicitColumnMode?: boolean;
  activeXGuide?: ActiveAlignmentGuide;
  activeYGuide?: ActiveAlignmentGuide;
  verticalAlignmentDebug?: VerticalAlignmentDebug;
  verticalSpacingDebug?: VerticalSpacingDebug;
  gapGuides: GapGuide[];
  gapDebug: GapGuideDebug[];
  spacingEqual: boolean;
  spacingEqualX?: boolean;
  spacingEqualY?: boolean;
  spacingEqualAll?: boolean;
  boxSource: AlignmentBoxSource;
  coordinateSpace: 'section-layer-px';
};

function finiteBox(box: AlignmentBox) {
  return (
    Number.isFinite(box.left) &&
    Number.isFinite(box.top) &&
    Number.isFinite(box.right) &&
    Number.isFinite(box.bottom) &&
    Number.isFinite(box.width) &&
    Number.isFinite(box.height)
  );
}

function rangesOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number) {
  return Math.min(aEnd, bEnd) - Math.max(aStart, bStart);
}

function getAnchorValue(box: AlignmentBox, anchor: BoxAnchor) {
  switch (anchor) {
    case 'left':
      return box.left;
    case 'right':
      return box.right;
    case 'centerX':
      return box.centerX;
    case 'top':
      return box.top;
    case 'bottom':
      return box.bottom;
    case 'centerY':
      return box.centerY;
  }
}

function isSectionAnchor(anchor: TargetAnchor) {
  return anchor.startsWith('section');
}

function anchorToGuideKind(anchor: TargetAnchor): AlignmentGuideKind {
  if (anchor === 'sectionCenterX' || anchor === 'sectionCenterY' || anchor === 'centerX' || anchor === 'centerY') {
    return isSectionAnchor(anchor) ? 'section-center' : 'block-center';
  }
  return isSectionAnchor(anchor) ? 'section-edge' : 'block-edge';
}

function isSameRow(movingBox: AlignmentBox, box: AlignmentBox) {
  const verticalOverlap = rangesOverlap(movingBox.top, movingBox.bottom, box.top, box.bottom);
  if (verticalOverlap > 0) return true;
  const rowTolerance = Math.max(
    ROW_COLUMN_TOLERANCE_PX,
    Math.min(movingBox.height, box.height) * 0.55
  );
  return Math.abs(movingBox.centerY - box.centerY) <= rowTolerance;
}

function sharesColumn(movingBox: AlignmentBox, box: AlignmentBox) {
  const horizontalOverlap = rangesOverlap(movingBox.left, movingBox.right, box.left, box.right);
  if (horizontalOverlap > 0) return true;
  const colTolerance = Math.max(
    ROW_COLUMN_TOLERANCE_PX,
    Math.min(movingBox.width, box.width) * 0.55
  );
  return Math.abs(movingBox.centerX - box.centerX) <= colTolerance;
}

function containsMovingBox(container: AlignmentBox, moving: AlignmentBox) {
  return (
    container.left <= moving.left + CONTAIN_TOLERANCE_PX &&
    container.right >= moving.right - CONTAIN_TOLERANCE_PX &&
    container.top <= moving.top + CONTAIN_TOLERANCE_PX &&
    container.bottom >= moving.bottom - CONTAIN_TOLERANCE_PX
  );
}

function isLargeContainer(box: AlignmentBox, movingBox: AlignmentBox, sectionWidth: number) {
  if (sectionWidth > 0 && box.width >= sectionWidth * LARGE_CONTAINER_WIDTH_RATIO) return true;
  if (
    box.width > movingBox.width * 4 &&
    sectionWidth > 0 &&
    box.width >= sectionWidth * 0.4
  ) {
    return true;
  }
  return false;
}

function hasSimilarRowSize(box: AlignmentBox, movingBox: AlignmentBox) {
  const widthRatio = box.width / movingBox.width;
  const heightRatio = box.height / movingBox.height;
  return (
    widthRatio >= SIMILAR_SIZE_MIN_RATIO &&
    widthRatio <= SIMILAR_SIZE_MAX_RATIO &&
    heightRatio >= SIMILAR_SIZE_MIN_RATIO &&
    heightRatio <= SIMILAR_SIZE_MAX_RATIO
  );
}

function evaluateRowGapCandidate(
  movingBox: AlignmentBox,
  box: AlignmentBox,
  sectionWidth: number
): CandidateRejectionReason | null {
  if (!isSameRow(movingBox, box)) return 'not-same-row';
  if (containsMovingBox(box, movingBox)) return 'contains-moving-box';
  if (isLargeContainer(box, movingBox, sectionWidth)) return 'large-container';
  if (!hasSimilarRowSize(box, movingBox)) return 'lower-priority-kind';
  return null;
}

function partitionRowGapCandidates(
  movingBox: AlignmentBox,
  boxes: AlignmentBox[],
  sectionWidth: number
): {
  accepted: AlignmentBox[];
  rejections: CandidateRejection[];
} {
  const accepted: AlignmentBox[] = [];
  const rejections: CandidateRejection[] = [];

  for (const box of boxes) {
    if (!finiteBox(box) || box.id === movingBox.id) continue;
    const reason = evaluateRowGapCandidate(movingBox, box, sectionWidth);
    if (reason) {
      rejections.push({ blockId: box.id ?? '', reason });
    } else {
      accepted.push(box);
    }
  }

  return { accepted, rejections };
}

function evaluateColumnGapCandidate(
  movingBox: AlignmentBox,
  box: AlignmentBox,
  sectionWidth: number
): CandidateRejectionReason | null {
  if (!sharesColumn(movingBox, box)) return 'not-same-column';
  if (containsMovingBox(box, movingBox)) return 'contains-moving-box';
  if (isLargeContainer(box, movingBox, sectionWidth)) return 'large-container';
  if (!hasSimilarRowSize(box, movingBox)) return 'lower-priority-kind';
  return null;
}

function partitionColumnGapCandidates(
  movingBox: AlignmentBox,
  boxes: AlignmentBox[],
  sectionWidth: number
): {
  accepted: AlignmentBox[];
  rejections: CandidateRejection[];
} {
  const accepted: AlignmentBox[] = [];
  const rejections: CandidateRejection[] = [];

  for (const box of boxes) {
    if (!finiteBox(box) || box.id === movingBox.id) continue;
    const reason = evaluateColumnGapCandidate(movingBox, box, sectionWidth);
    if (reason) {
      rejections.push({ blockId: box.id ?? '', reason });
    } else {
      accepted.push(box);
    }
  }

  return { accepted, rejections };
}

function findImmediateHorizontalNeighbor(
  movingBox: AlignmentBox,
  candidates: AlignmentBox[],
  side: 'left' | 'right',
  options?: { requireSameRow?: boolean }
): AlignmentBox | null {
  const requireSameRow = options?.requireSameRow ?? true;

  if (side === 'left') {
    let best: AlignmentBox | null = null;
    let bestRight = -Infinity;

    for (const box of candidates) {
      if (!finiteBox(box)) continue;
      if (requireSameRow && !isSameRow(movingBox, box)) continue;
      if (box.centerX >= movingBox.centerX) continue;
      if (box.right > bestRight) {
        bestRight = box.right;
        best = box;
      }
    }

    return best;
  }

  let best: AlignmentBox | null = null;
  let bestLeft = Infinity;

  for (const box of candidates) {
    if (!finiteBox(box)) continue;
    if (requireSameRow && !isSameRow(movingBox, box)) continue;
    if (box.centerX <= movingBox.centerX) continue;
    if (box.left < bestLeft) {
      bestLeft = box.left;
      best = box;
    }
  }

  return best;
}

function collectNonImmediateRejections(
  movingBox: AlignmentBox,
  accepted: AlignmentBox[],
  leftNeighbor: AlignmentBox | null,
  rightNeighbor: AlignmentBox | null
): CandidateRejection[] {
  const immediateIds = new Set(
    [leftNeighbor?.id, rightNeighbor?.id].filter((id): id is string => Boolean(id))
  );
  const rejections: CandidateRejection[] = [];

  for (const box of accepted) {
    if (!box.id || immediateIds.has(box.id)) continue;
    rejections.push({ blockId: box.id, reason: 'not-immediate-neighbour' });
  }

  return rejections;
}

function collectVerticalNonImmediateRejections(
  accepted: AlignmentBox[],
  topNeighbor: AlignmentBox | null,
  bottomNeighbor: AlignmentBox | null
): CandidateRejection[] {
  const immediateIds = new Set(
    [topNeighbor?.id, bottomNeighbor?.id].filter((id): id is string => Boolean(id))
  );
  const rejections: CandidateRejection[] = [];

  for (const box of accepted) {
    if (!box.id || immediateIds.has(box.id)) continue;
    rejections.push({ blockId: box.id, reason: 'not-immediate-neighbour' });
  }

  return rejections;
}

function findImmediateVerticalNeighbor(
  movingBox: AlignmentBox,
  candidates: AlignmentBox[],
  side: 'top' | 'bottom',
  options?: { requireSharesColumn?: boolean; excludeSameRow?: boolean }
): AlignmentBox | null {
  const requireSharesColumn = options?.requireSharesColumn ?? true;
  const excludeSameRow = options?.excludeSameRow ?? false;

  if (side === 'top') {
    let best: AlignmentBox | null = null;
    let bestBottom = -Infinity;

    for (const box of candidates) {
      if (!finiteBox(box)) continue;
      if (excludeSameRow && isSameRow(movingBox, box)) continue;
      if (requireSharesColumn && !sharesColumn(movingBox, box)) continue;
      if (box.centerY >= movingBox.centerY) continue;
      if (box.bottom > bestBottom) {
        bestBottom = box.bottom;
        best = box;
      }
    }

    return best;
  }

  let best: AlignmentBox | null = null;
  let bestTop = Infinity;

  for (const box of candidates) {
    if (!finiteBox(box)) continue;
    if (excludeSameRow && isSameRow(movingBox, box)) continue;
    if (requireSharesColumn && !sharesColumn(movingBox, box)) continue;
    if (box.centerY <= movingBox.centerY) continue;
    if (box.top < bestTop) {
      bestTop = box.top;
      best = box;
    }
  }

  return best;
}

type AlignmentMatchCandidate = {
  axis: AlignmentGuideAxis;
  movingAnchor: BoxAnchor;
  targetBlockId?: string;
  targetAnchor: TargetAnchor;
  targetValue: number;
  movingValue: number;
  distancePx: number;
  snapDeltaPx: number;
  targetBox?: AlignmentBox;
};

function findNearestVerticalNeighbor(
  movingBox: AlignmentBox,
  boxes: AlignmentBox[],
  side: 'top' | 'bottom'
): AlignmentBox | null {
  let best: AlignmentBox | null = null;
  let bestGap = Infinity;

  for (const box of boxes) {
    if (!finiteBox(box)) continue;

    if (side === 'top') {
      if (!sharesColumn(movingBox, box)) continue;
      if (box.centerY >= movingBox.centerY) continue;
      const gap = movingBox.top - box.bottom;
      if (gap < bestGap) {
        bestGap = gap;
        best = box;
      }
      continue;
    }

    if (!sharesColumn(movingBox, box)) continue;
    if (box.centerY <= movingBox.centerY) continue;
    const gap = box.top - movingBox.bottom;
    if (gap < bestGap) {
      bestGap = gap;
      best = box;
    }
  }

  return best;
}

type GapDisplay = {
  gapPx: number;
  label: string;
  overlap: boolean;
  touch: boolean;
};

function resolveGapDisplay(computedGap: number): GapDisplay {
  if (!Number.isFinite(computedGap)) {
    return { gapPx: 0, label: '0', overlap: false, touch: true };
  }
  const rounded = Math.round(computedGap);
  if (rounded > GAP_TOUCH_TOLERANCE_PX) {
    return { gapPx: rounded, label: `${rounded}px`, overlap: false, touch: false };
  }
  if (Math.abs(rounded) <= GAP_TOUCH_TOLERANCE_PX) {
    return { gapPx: 0, label: '0', overlap: false, touch: true };
  }
  if (rounded > -OVERLAP_DISPLAY_THRESHOLD_PX) {
    return { gapPx: 0, label: '0', overlap: false, touch: true };
  }
  const overlapPx = Math.abs(rounded);
  return {
    gapPx: rounded,
    label: `${overlapPx}px overlap`,
    overlap: true,
    touch: false,
  };
}

function shouldShowRowGapGuide(
  computedGap: number,
  otherComputedGap: number | null
): boolean {
  if (computedGap > GAP_TOUCH_TOLERANCE_PX) {
    return true;
  }
  if (Math.abs(computedGap) <= GAP_TOUCH_TOLERANCE_PX) {
    return true;
  }
  if (computedGap > -OVERLAP_DISPLAY_THRESHOLD_PX) {
    return false;
  }
  if (otherComputedGap !== null && otherComputedGap > GAP_TOUCH_TOLERANCE_PX) {
    return false;
  }
  return true;
}

function formatGapLabel(computedGap: number) {
  return resolveGapDisplay(computedGap).label;
}

function isHoldingButtonRowBlock(blockId?: string) {
  return Boolean(blockId && ROW_HORIZONTAL_RHYTHM_BLOCK_IDS.has(blockId));
}

function shouldUseRowHorizontalRhythm(movingBlockId?: string, explicitRowMode?: boolean): boolean {
  return explicitRowMode || isHoldingButtonRowBlock(movingBlockId);
}

function resolveLayoutRhythmGapOptions(
  movingBlockId?: string,
  explicitRowMode?: boolean
): { rowHorizontalGapsOnly?: boolean } | undefined {
  return shouldUseRowHorizontalRhythm(movingBlockId, explicitRowMode)
    ? { rowHorizontalGapsOnly: true }
    : undefined;
}

function isPrimarilyVerticalNeighbor(
  movingBox: AlignmentBox,
  neighbor: AlignmentBox,
  side: 'top' | 'bottom'
) {
  const verticalSeparation =
    side === 'top'
      ? movingBox.centerY - neighbor.centerY
      : neighbor.centerY - movingBox.centerY;
  if (verticalSeparation <= 0) return false;
  const horizontalSeparation = Math.abs(movingBox.centerX - neighbor.centerX);
  return verticalSeparation >= horizontalSeparation;
}

function isPrimarilyHorizontalNeighbor(
  movingBox: AlignmentBox,
  neighbor: AlignmentBox,
  side: 'left' | 'right'
) {
  const horizontalSeparation =
    side === 'left'
      ? movingBox.centerX - neighbor.centerX
      : neighbor.centerX - movingBox.centerX;
  if (horizontalSeparation <= 0) return false;
  const verticalSeparation = Math.abs(movingBox.centerY - neighbor.centerY);
  return horizontalSeparation >= verticalSeparation;
}

function resolveHoldingButtonStackMode(
  movingBox: AlignmentBox,
  stackPeers: AlignmentBox[],
  topPeer: AlignmentBox | null,
  bottomPeer: AlignmentBox | null,
  leftPeer: AlignmentBox | null,
  rightPeer: AlignmentBox | null
): 'row' | 'column' {
  const rowLeftPeer = leftPeer;
  const rowRightPeer = rightPeer;
  const columnTopPeer =
    topPeer &&
    !isSameRow(movingBox, topPeer) &&
    isPrimarilyVerticalNeighbor(movingBox, topPeer, 'top')
      ? topPeer
      : null;
  const columnBottomPeer =
    bottomPeer &&
    !isSameRow(movingBox, bottomPeer) &&
    isPrimarilyVerticalNeighbor(movingBox, bottomPeer, 'bottom')
      ? bottomPeer
      : null;

  if (columnTopPeer && columnBottomPeer) {
    return 'column';
  }
  if (rowLeftPeer && rowRightPeer) {
    return 'row';
  }
  if (columnTopPeer || columnBottomPeer) {
    return 'column';
  }
  if (rowLeftPeer || rowRightPeer) {
    return 'row';
  }

  const peerCenters = stackPeers
    .filter((peer) => peer.id !== movingBox.id)
    .map((peer) => ({ centerX: peer.centerX, centerY: peer.centerY }));
  if (peerCenters.length === 0) {
    return 'row';
  }

  const centerXs = [movingBox.centerX, ...peerCenters.map((peer) => peer.centerX)];
  const centerYs = [movingBox.centerY, ...peerCenters.map((peer) => peer.centerY)];
  const spreadX = Math.max(...centerXs) - Math.min(...centerXs);
  const spreadY = Math.max(...centerYs) - Math.min(...centerYs);
  return spreadY > spreadX ? 'column' : 'row';
}

function resolveGapPeerContext(
  movingBox: AlignmentBox,
  neighbors: AlignmentBox[],
  sectionWidth: number,
  movingBlockId?: string
): {
  horizontalCandidates: AlignmentBox[];
  verticalCandidates: AlignmentBox[];
  rejections: CandidateRejection[];
  explicitRowMode: boolean;
  explicitColumnMode: boolean;
  stackPeerIds: string[];
} {
  const resolvedMovingId = movingBox.id ?? movingBlockId;
  if (isHoldingButtonRowBlock(resolvedMovingId)) {
    const stackPeers = neighbors.filter(
      (box) => box.id && ROW_HORIZONTAL_RHYTHM_BLOCK_IDS.has(box.id)
    );
    const rejections = neighbors
      .filter(
        (box) =>
          box.id &&
          box.id !== movingBox.id &&
          !stackPeers.some((peer) => peer.id === box.id)
      )
      .map((box) => ({ blockId: box.id!, reason: 'not-row-peer' as const }));

    const leftPeer = findImmediateHorizontalNeighbor(movingBox, stackPeers, 'left', {
      requireSameRow: false,
    });
    const rightPeer = findImmediateHorizontalNeighbor(movingBox, stackPeers, 'right', {
      requireSameRow: false,
    });
    const topPeer = findImmediateVerticalNeighbor(movingBox, stackPeers, 'top', {
      requireSharesColumn: false,
      excludeSameRow: true,
    });
    const bottomPeer = findImmediateVerticalNeighbor(movingBox, stackPeers, 'bottom', {
      requireSharesColumn: false,
      excludeSameRow: true,
    });
    const stackMode = resolveHoldingButtonStackMode(
      movingBox,
      stackPeers,
      topPeer,
      bottomPeer,
      leftPeer,
      rightPeer
    );
    const explicitColumnMode = stackMode === 'column';
    const explicitRowMode = stackMode === 'row';

    return {
      horizontalCandidates: stackPeers,
      verticalCandidates: stackPeers,
      rejections,
      explicitRowMode,
      explicitColumnMode,
      stackPeerIds: stackPeers.map((box) => box.id!).filter(Boolean),
    };
  }

  const { accepted: rowAccepted, rejections: rowRejections } = partitionRowGapCandidates(
    movingBox,
    neighbors,
    sectionWidth
  );
  const { accepted: columnAccepted, rejections: columnRejections } = partitionColumnGapCandidates(
    movingBox,
    neighbors,
    sectionWidth
  );

  return {
    horizontalCandidates: rowAccepted,
    verticalCandidates: columnAccepted,
    rejections: [...rowRejections, ...columnRejections],
    explicitRowMode: false,
    explicitColumnMode: false,
    stackPeerIds: [],
  };
}

function buildVerticalSpacingDebug({
  movingBlockId,
  movingBox,
  movingBoxSource,
  stackPeerIds,
  topNeighbor,
  bottomNeighbor,
  topComputedGap,
  bottomComputedGap,
  topDisplay,
  bottomDisplay,
  equalSpacingRendered,
  rejectedCandidates,
}: {
  movingBlockId?: string;
  movingBox: AlignmentBox;
  movingBoxSource: AlignmentBoxSource;
  stackPeerIds: string[];
  topNeighbor: AlignmentBox | null;
  bottomNeighbor: AlignmentBox | null;
  topComputedGap: number | null;
  bottomComputedGap: number | null;
  topDisplay: GapDisplay | null;
  bottomDisplay: GapDisplay | null;
  equalSpacingRendered: boolean;
  rejectedCandidates: CandidateRejection[];
}): VerticalSpacingDebug {
  const debug: VerticalSpacingDebug = {
    movingBlockId,
    movingBox: serializeAlignmentBox(movingBox),
    stackPeerIds,
    rejectedCandidates: rejectedCandidates.map((entry) => ({
      blockId: entry.blockId,
      reason: entry.reason,
    })),
  };

  if (topNeighbor && topComputedGap !== null && topDisplay) {
    debug.topNeighbour = {
      blockId: topNeighbor.id,
      gapPx: topDisplay.gapPx,
      overlap: topDisplay.overlap,
      chosenBoxSource: movingBoxSource,
    };
  }

  if (bottomNeighbor && bottomComputedGap !== null && bottomDisplay) {
    debug.bottomNeighbour = {
      blockId: bottomNeighbor.id,
      gapPx: bottomDisplay.gapPx,
      overlap: bottomDisplay.overlap,
      chosenBoxSource: movingBoxSource,
    };
  }

  if (
    topDisplay &&
    bottomDisplay &&
    topComputedGap !== null &&
    bottomComputedGap !== null &&
    topDisplay.gapPx >= 0 &&
    bottomDisplay.gapPx >= 0 &&
    !topDisplay.overlap &&
    !bottomDisplay.overlap
  ) {
    const differencePx = Math.abs(topDisplay.gapPx - bottomDisplay.gapPx);
    debug.equalSpacing = {
      topGapPx: topDisplay.gapPx,
      bottomGapPx: bottomDisplay.gapPx,
      differencePx,
      withinTolerance: topDisplay.gapPx === bottomDisplay.gapPx,
      rendered: equalSpacingRendered,
    };
  }

  return debug;
}

function computePeerHorizontalGapGeometry(leftBox: AlignmentBox, rightBox: AlignmentBox) {
  const rowOverlapTop = Math.max(leftBox.top, rightBox.top);
  const rowOverlapBottom = Math.min(leftBox.bottom, rightBox.bottom);
  const rowCenterY =
    rowOverlapBottom > rowOverlapTop
      ? (rowOverlapTop + rowOverlapBottom) / 2
      : (leftBox.centerY + rightBox.centerY) / 2;

  const edgeA = leftBox.right;
  const edgeB = rightBox.left;

  return {
    start: Math.min(edgeA, edgeB),
    end: Math.max(edgeA, edgeB),
    labelPosition: { x: (edgeA + edgeB) / 2, y: rowCenterY },
    guideLineY: rowCenterY,
    edgeMarkerStart: rowCenterY - GAP_TICK_HALF_HEIGHT_PX,
    edgeMarkerEnd: rowCenterY + GAP_TICK_HALF_HEIGHT_PX,
  };
}

function computePeerVerticalGapGeometry(topBox: AlignmentBox, bottomBox: AlignmentBox) {
  const columnOverlapLeft = Math.max(topBox.left, bottomBox.left);
  const columnOverlapRight = Math.min(topBox.right, bottomBox.right);
  const columnCenterX =
    columnOverlapRight > columnOverlapLeft
      ? (columnOverlapLeft + columnOverlapRight) / 2
      : (topBox.centerX + bottomBox.centerX) / 2;

  const edgeA = topBox.bottom;
  const edgeB = bottomBox.top;

  return {
    start: Math.min(edgeA, edgeB),
    end: Math.max(edgeA, edgeB),
    labelPosition: { x: columnCenterX, y: (edgeA + edgeB) / 2 },
    guideLineX: columnCenterX,
    edgeMarkerStart: columnCenterX - GAP_TICK_HALF_HEIGHT_PX,
    edgeMarkerEnd: columnCenterX + GAP_TICK_HALF_HEIGHT_PX,
  };
}

function gapGuideGeometryKey(guide: GapGuide) {
  return `${guide.axis}:${Math.round(guide.start)}:${Math.round(guide.end)}`;
}

function mergeGapGuides(existing: GapGuide[], incoming: GapGuide[]) {
  const keys = new Set(existing.map(gapGuideGeometryKey));
  for (const guide of incoming) {
    const key = gapGuideGeometryKey(guide);
    if (keys.has(key)) continue;
    existing.push(guide);
    keys.add(key);
  }
}

function buildPeerHorizontalGapGuide(
  leftBox: AlignmentBox,
  rightBox: AlignmentBox,
  movingBox: AlignmentBox
): GapGuide | null {
  const computedGap = rightBox.left - leftBox.right;
  if (!shouldShowRowGapGuide(computedGap, null)) return null;

  const display = resolveGapDisplay(computedGap);
  const geometry = computePeerHorizontalGapGeometry(leftBox, rightBox);
  const side: GapGuide['side'] = leftBox.id === movingBox.id ? 'right' : 'left';
  const neighbourBlockId = rightBox.id === movingBox.id ? leftBox.id : rightBox.id;
  const debug = buildHorizontalGapDebug(side, movingBox, rightBox, computedGap, display.label);

  return {
    axis: 'x',
    side,
    neighbourBlockId,
    gapPx: display.gapPx,
    start: geometry.start,
    end: geometry.end,
    labelPosition: geometry.labelPosition,
    overlap: display.overlap,
    touch: display.touch,
    displayLabel: display.label,
    guideLineY: geometry.guideLineY,
    edgeMarkerStart: geometry.edgeMarkerStart,
    edgeMarkerEnd: geometry.edgeMarkerEnd,
    involvesMovingBox: leftBox.id === movingBox.id || rightBox.id === movingBox.id,
    debug,
  };
}

function buildPeerVerticalGapGuide(
  topBox: AlignmentBox,
  bottomBox: AlignmentBox,
  movingBox: AlignmentBox
): GapGuide | null {
  const computedGap = bottomBox.top - topBox.bottom;
  if (!shouldShowRowGapGuide(computedGap, null)) return null;

  const display = resolveGapDisplay(computedGap);
  const geometry = computePeerVerticalGapGeometry(topBox, bottomBox);
  const side: GapGuide['side'] = topBox.id === movingBox.id ? 'bottom' : 'top';
  const neighbourBlockId = bottomBox.id === movingBox.id ? topBox.id : bottomBox.id;
  const debug = buildVerticalGapDebug(side, movingBox, bottomBox, computedGap, display.label);

  return {
    axis: 'y',
    side,
    neighbourBlockId,
    gapPx: display.gapPx,
    start: geometry.start,
    end: geometry.end,
    labelPosition: geometry.labelPosition,
    overlap: display.overlap,
    touch: display.touch,
    displayLabel: display.label,
    guideLineX: geometry.guideLineX,
    edgeMarkerStart: geometry.edgeMarkerStart,
    edgeMarkerEnd: geometry.edgeMarkerEnd,
    involvesMovingBox: topBox.id === movingBox.id || bottomBox.id === movingBox.id,
    debug,
  };
}

function calculateRowColumnDistributionGapGuides(
  movingBox: AlignmentBox,
  referenceBoxes: AlignmentBox[]
): GapGuide[] {
  const guides: GapGuide[] = [];

  const rowPeers = referenceBoxes.filter(
    (box) => finiteBox(box) && box.id && box.id !== movingBox.id && isSameRow(movingBox, box)
  );
  if (rowPeers.length > 0) {
    const rowChain = [...rowPeers, movingBox].sort((left, right) => left.left - right.left);
    for (let index = 0; index < rowChain.length - 1; index += 1) {
      const leftBox = rowChain[index];
      const rightBox = rowChain[index + 1];
      if (leftBox.id !== movingBox.id && rightBox.id !== movingBox.id) continue;
      const guide = buildPeerHorizontalGapGuide(leftBox, rightBox, movingBox);
      if (guide) guides.push(guide);
    }
  }

  const columnPeers = referenceBoxes.filter(
    (box) => finiteBox(box) && box.id && box.id !== movingBox.id && sharesColumn(movingBox, box)
  );
  if (columnPeers.length > 0) {
    const columnChain = [...columnPeers, movingBox].sort((top, bottom) => top.top - bottom.top);
    for (let index = 0; index < columnChain.length - 1; index += 1) {
      const topBox = columnChain[index];
      const bottomBox = columnChain[index + 1];
      if (topBox.id !== movingBox.id && bottomBox.id !== movingBox.id) continue;
      const guide = buildPeerVerticalGapGuide(topBox, bottomBox, movingBox);
      if (guide) guides.push(guide);
    }
  }

  return guides;
}

function applyRowColumnChainEqualSpacing(guides: GapGuide[]) {
  const horizontalGuides = guides.filter(
    (guide) =>
      guide.axis === 'x' &&
      guide.involvesMovingBox &&
      !guide.overlap &&
      !guide.touch &&
      guide.gapPx > GAP_TOUCH_TOLERANCE_PX
  );
  if (
    horizontalGuides.length >= 2 &&
    horizontalGuides.every((guide) => guide.gapPx === horizontalGuides[0].gapPx)
  ) {
    horizontalGuides.forEach((guide) => {
      if (!guide.equalGroupId) {
        guide.equalGroupId = 'equal-x';
      }
    });
  }

  const verticalGuides = guides.filter(
    (guide) =>
      guide.axis === 'y' &&
      guide.involvesMovingBox &&
      !guide.overlap &&
      !guide.touch &&
      guide.gapPx > GAP_TOUCH_TOLERANCE_PX
  );
  if (
    verticalGuides.length >= 2 &&
    verticalGuides.every((guide) => guide.gapPx === verticalGuides[0].gapPx)
  ) {
    const verticalValue = verticalGuides[0].gapPx;
    const horizontalEqualValue = horizontalGuides.find((guide) => guide.equalGroupId === 'equal-x')?.gapPx;
    const verticalGroupId =
      horizontalEqualValue !== undefined && horizontalEqualValue === verticalValue
        ? 'equal-y'
        : 'equal-y-axis';
    verticalGuides.forEach((guide) => {
      if (!guide.equalGroupId) {
        guide.equalGroupId = verticalGroupId;
      }
    });
  }
}

function computeHorizontalGapGeometry(
  movingBox: AlignmentBox,
  neighbor: AlignmentBox,
  side: 'left' | 'right'
) {
  const rowOverlapTop = Math.max(neighbor.top, movingBox.top);
  const rowOverlapBottom = Math.min(neighbor.bottom, movingBox.bottom);
  const rowCenterY =
    rowOverlapBottom > rowOverlapTop
      ? (rowOverlapTop + rowOverlapBottom) / 2
      : (neighbor.centerY + movingBox.centerY) / 2;

  const edgeA = side === 'left' ? neighbor.right : movingBox.right;
  const edgeB = side === 'left' ? movingBox.left : neighbor.left;

  return {
    start: Math.min(edgeA, edgeB),
    end: Math.max(edgeA, edgeB),
    labelPosition: { x: (edgeA + edgeB) / 2, y: rowCenterY },
    guideLineY: rowCenterY,
    edgeMarkerStart: rowCenterY - GAP_TICK_HALF_HEIGHT_PX,
    edgeMarkerEnd: rowCenterY + GAP_TICK_HALF_HEIGHT_PX,
  };
}

function computeVerticalGapGeometry(
  movingBox: AlignmentBox,
  neighbor: AlignmentBox,
  side: 'top' | 'bottom'
) {
  const columnOverlapLeft = Math.max(neighbor.left, movingBox.left);
  const columnOverlapRight = Math.min(neighbor.right, movingBox.right);
  const columnCenterX =
    columnOverlapRight > columnOverlapLeft
      ? (columnOverlapLeft + columnOverlapRight) / 2
      : (neighbor.centerX + movingBox.centerX) / 2;

  const edgeA = side === 'top' ? neighbor.bottom : movingBox.bottom;
  const edgeB = side === 'top' ? movingBox.top : neighbor.top;

  return {
    start: Math.min(edgeA, edgeB),
    end: Math.max(edgeA, edgeB),
    labelPosition: { x: columnCenterX, y: (edgeA + edgeB) / 2 },
    guideLineX: columnCenterX,
    edgeMarkerStart: columnCenterX - GAP_TICK_HALF_HEIGHT_PX,
    edgeMarkerEnd: columnCenterX + GAP_TICK_HALF_HEIGHT_PX,
  };
}

function buildHorizontalGapDebug(
  side: 'left' | 'right',
  movingBox: AlignmentBox,
  neighbor: AlignmentBox,
  computedGap: number,
  displayedLabel: string
): GapGuideDebug {
  return {
    side,
    neighbourBlockId: neighbor.id,
    movingLeft: movingBox.left,
    movingRight: movingBox.right,
    neighbourLeft: neighbor.left,
    neighbourRight: neighbor.right,
    computedGap,
    displayedLabel,
    coordinateSpace: 'section-layer-px',
    boxSource: 'active',
    overlap: computedGap < 0,
  };
}

function buildVerticalGapDebug(
  side: 'top' | 'bottom',
  movingBox: AlignmentBox,
  neighbor: AlignmentBox,
  computedGap: number,
  displayedLabel: string
): GapGuideDebug {
  return {
    side,
    neighbourBlockId: neighbor.id,
    movingLeft: movingBox.left,
    movingRight: movingBox.right,
    neighbourLeft: neighbor.left,
    neighbourRight: neighbor.right,
    computedGap,
    displayedLabel,
    coordinateSpace: 'section-layer-px',
    boxSource: 'active',
    overlap: computedGap < 0,
  };
}

export function createReferenceFrameBoxes(
  referenceBlocks: AlignmentLayoutBlock[],
  colWidth: number,
  rowHeight: number,
  frameBoxesByBlockId: Record<string, AlignmentBox | undefined>
): AlignmentBox[] {
  return referenceBlocks.map((block) => {
    const frame = frameBoxesByBlockId[block.id];
    if (frame && finiteBox(frame)) {
      return { ...frame, id: block.id };
    }
    return createAlignmentBoxFromLayout(block.layout, colWidth, rowHeight, {
      id: block.id,
      leftPx: block.leftPx,
      topPx: block.topPx,
      offsetXPx: block.offsetXPx,
      offsetYPx: block.offsetYPx,
      widthPx: block.widthPx,
      heightPx: block.heightPx,
    });
  });
}

const Y_BACKGROUND_BLOCK_KINDS = new Set([
  'model-viewer',
  'spacer',
  'decorative-asset',
  'gallery',
  'footer',
]);

/** Max perpendicular gap before alignment/snap ignores a neighbour (title vs footer). */
export const ALIGNMENT_MAX_PEER_SEPARATION_PX = 280;
export const ALIGNMENT_MAX_PEER_SEPARATION_RATIO = 0.42;

function resolveAlignmentMaxPeerSeparation(sectionSpan: number): number {
  return Math.min(
    ALIGNMENT_MAX_PEER_SEPARATION_PX,
    Math.max(120, sectionSpan * ALIGNMENT_MAX_PEER_SEPARATION_RATIO)
  );
}

function perpendicularGapBetweenBoxes(
  movingBox: AlignmentBox,
  neighbor: AlignmentBox,
  alignmentAxis: 'x' | 'y'
): number {
  if (alignmentAxis === 'x') {
    if (movingBox.bottom <= neighbor.top) return neighbor.top - movingBox.bottom;
    if (neighbor.bottom <= movingBox.top) return movingBox.top - neighbor.bottom;
    return 0;
  }
  if (movingBox.right <= neighbor.left) return neighbor.left - movingBox.right;
  if (neighbor.right <= movingBox.left) return movingBox.left - neighbor.right;
  return 0;
}

function alignmentContextGap(
  movingBox: AlignmentBox,
  neighbor: AlignmentBox,
  alignmentAxis: 'x' | 'y'
): number {
  const edgeGap = perpendicularGapBetweenBoxes(movingBox, neighbor, alignmentAxis);
  if (edgeGap > 0) return edgeGap;
  if (alignmentAxis === 'x') {
    return Math.abs(movingBox.centerY - neighbor.centerY);
  }
  return Math.abs(movingBox.centerX - neighbor.centerX);
}

export function isAlignmentNeighborWithinReach(
  movingBox: AlignmentBox,
  neighbor: AlignmentBox,
  alignmentAxis: 'x' | 'y',
  sectionSpan: number,
  rowPeerIds: Set<string>
): boolean {
  if (neighbor.id && rowPeerIds.has(neighbor.id)) return true;
  if (isSameRow(movingBox, neighbor)) return true;
  const gap = alignmentContextGap(movingBox, neighbor, alignmentAxis);
  return gap <= resolveAlignmentMaxPeerSeparation(sectionSpan);
}

const Y_PRIORITY_RANK: Record<VerticalAlignmentYPriority | 'excluded' | 'section-anchor', number> = {
  'row-peer': 0,
  'same-column-similar': 1,
  'same-column': 2,
  'same-row': 3,
  'nearby-content': 4,
  'section-anchor': 5,
  excluded: 99,
};

function alignmentNeighborPriority(neighbor: AlignmentBox, rowPeerIds: Set<string>): number {
  if (neighbor.id && rowPeerIds.has(neighbor.id)) return 0;
  return 2;
}

function alignmentMatchPriority(
  match: AlignmentMatchCandidate,
  rowPeerIds: Set<string>,
  restrictToRowPeers: boolean
): number {
  if (!match.targetBox) return restrictToRowPeers ? 1 : 0;
  return alignmentNeighborPriority(match.targetBox, rowPeerIds);
}

function beatsAlignmentMatch(
  candidate: AlignmentMatchCandidate,
  best: AlignmentMatchCandidate | null,
  rowPeerIds: Set<string>,
  restrictToRowPeers: boolean
): boolean {
  if (!best) return true;
  const candidatePriority = alignmentMatchPriority(candidate, rowPeerIds, restrictToRowPeers);
  const bestPriority = alignmentMatchPriority(best, rowPeerIds, restrictToRowPeers);
  return (
    candidatePriority < bestPriority ||
    (candidatePriority === bestPriority && candidate.distancePx < best.distancePx)
  );
}

function evaluateYAlignmentCandidate(
  movingBox: AlignmentBox,
  neighbor: AlignmentBox,
  movingBlockId?: string,
  rowPeerIds: Set<string>,
  blockKind?: string,
  sectionWidth: number
): { priority: VerticalAlignmentYPriority | 'excluded'; rejectionReason?: string } {
  if (!neighbor.id || neighbor.id === movingBlockId) {
    return { priority: 'excluded', rejectionReason: 'self' };
  }

  if (containsMovingBox(neighbor, movingBox)) {
    return { priority: 'excluded', rejectionReason: 'contains-moving-box' };
  }

  const isRowPeer = rowPeerIds.has(neighbor.id);
  const isBackgroundKind = Boolean(blockKind && Y_BACKGROUND_BLOCK_KINDS.has(blockKind));

  if (isBackgroundKind && !isRowPeer) {
    return { priority: 'excluded', rejectionReason: 'decorative-background' };
  }

  if (isLargeContainer(neighbor, movingBox, sectionWidth) && !isRowPeer) {
    return { priority: 'excluded', rejectionReason: 'large-container' };
  }

  if (isRowPeer) {
    return { priority: 'row-peer' };
  }

  if (sharesColumn(movingBox, neighbor) && hasSimilarRowSize(neighbor, movingBox)) {
    return { priority: 'same-column-similar' };
  }

  if (sharesColumn(movingBox, neighbor)) {
    return { priority: 'same-column' };
  }

  if (isSameRow(movingBox, neighbor)) {
    return { priority: 'same-row' };
  }

  return { priority: 'nearby-content' };
}

function beatsYBlockMatch(
  candidate: AlignmentMatchCandidate,
  candidatePriority: VerticalAlignmentYPriority,
  best: AlignmentMatchCandidate | null,
  bestPriority: VerticalAlignmentYPriority | 'excluded'
): boolean {
  if (!best) return true;
  const candidateRank = Y_PRIORITY_RANK[candidatePriority];
  const bestRank = Y_PRIORITY_RANK[bestPriority];
  return candidateRank < bestRank || (candidateRank === bestRank && candidate.distancePx < best.distancePx);
}

function findBestXAlignmentMatch(
  movingBox: AlignmentBox,
  neighbors: AlignmentBox[],
  sectionWidth: number,
  sectionHeight: number,
  threshold: number,
  rowPeerIds: Set<string>,
  restrictNeighborsToRowPeers: boolean
): AlignmentMatchCandidate | null {
  const movingAnchors: BoxAnchor[] = ['left', 'right', 'centerX'];
  const candidateAnchors: BoxAnchor[] = ['left', 'right', 'centerX'];
  const blockNeighbors = restrictNeighborsToRowPeers
    ? neighbors.filter((neighbor) => neighbor.id && rowPeerIds.has(neighbor.id))
    : neighbors;

  let best: AlignmentMatchCandidate | null = null;

  for (const movingAnchor of movingAnchors) {
    const movingValue = getAnchorValue(movingBox, movingAnchor);

    for (const neighbor of blockNeighbors) {
      if (!isAlignmentNeighborWithinReach(movingBox, neighbor, 'x', sectionHeight, rowPeerIds)) {
        continue;
      }
      for (const targetAnchor of candidateAnchors) {
        const targetValue = getAnchorValue(neighbor, targetAnchor);
        const distancePx = Math.abs(movingValue - targetValue);
        if (distancePx > threshold) continue;
        const candidate: AlignmentMatchCandidate = {
          axis: 'x',
          movingAnchor,
          targetBlockId: neighbor.id,
          targetAnchor,
          targetValue,
          movingValue,
          distancePx,
          snapDeltaPx: targetValue - movingValue,
          targetBox: neighbor,
        };
        if (
          beatsAlignmentMatch(candidate, best, rowPeerIds, restrictNeighborsToRowPeers)
        ) {
          best = candidate;
        }
      }
    }

    if (!restrictNeighborsToRowPeers) {
      const sectionTargets: Array<{ anchor: SectionAnchor; value: number }> = [
        { anchor: 'sectionLeft', value: 0 },
        { anchor: 'sectionCenterX', value: sectionWidth / 2 },
        { anchor: 'sectionRight', value: sectionWidth },
      ];

      for (const section of sectionTargets) {
        const distancePx = Math.abs(movingValue - section.value);
        if (distancePx > threshold) continue;
        const candidate: AlignmentMatchCandidate = {
          axis: 'x',
          movingAnchor,
          targetAnchor: section.anchor,
          targetValue: section.value,
          movingValue,
          distancePx,
          snapDeltaPx: section.value - movingValue,
        };
        if (beatsAlignmentMatch(candidate, best, rowPeerIds, restrictNeighborsToRowPeers)) {
          best = candidate;
        }
      }
    }
  }

  return best;
}

function findBestYAlignmentMatch(
  movingBox: AlignmentBox,
  neighbors: AlignmentBox[],
  sectionWidth: number,
  sectionHeight: number,
  threshold: number,
  options: {
    movingBlockId?: string;
    rowPeerIds: Set<string>;
    explicitRowMode: boolean;
    explicitColumnMode: boolean;
    blockKindsById?: Record<string, string>;
  }
): AlignmentMatchCandidate | null {
  const movingAnchors: BoxAnchor[] = ['top', 'bottom', 'centerY'];
  const candidateAnchors: BoxAnchor[] = ['top', 'bottom', 'centerY'];

  let bestBlock: AlignmentMatchCandidate | null = null;
  let bestBlockPriority: VerticalAlignmentYPriority | 'excluded' = 'excluded';

  for (const neighbor of neighbors) {
    if (!isAlignmentNeighborWithinReach(movingBox, neighbor, 'y', sectionWidth, options.rowPeerIds)) {
      continue;
    }
    const evaluation = evaluateYAlignmentCandidate(
      movingBox,
      neighbor,
      options.movingBlockId,
      options.rowPeerIds,
      options.blockKindsById?.[neighbor.id ?? ''],
      sectionWidth
    );
    if (evaluation.priority === 'excluded') continue;

    for (const movingAnchor of movingAnchors) {
      const movingValue = getAnchorValue(movingBox, movingAnchor);
      for (const targetAnchor of candidateAnchors) {
        const targetValue = getAnchorValue(neighbor, targetAnchor);
        const distancePx = Math.abs(movingValue - targetValue);
        if (distancePx > threshold) continue;
        const candidate: AlignmentMatchCandidate = {
          axis: 'y',
          movingAnchor,
          targetBlockId: neighbor.id,
          targetAnchor,
          targetValue,
          movingValue,
          distancePx,
          snapDeltaPx: targetValue - movingValue,
          targetBox: neighbor,
        };
        if (beatsYBlockMatch(candidate, evaluation.priority, bestBlock, bestBlockPriority)) {
          bestBlock = candidate;
          bestBlockPriority = evaluation.priority;
        }
      }
    }
  }

  let bestSection: AlignmentMatchCandidate | null = null;
  if (!options.explicitRowMode && !options.explicitColumnMode) {
    for (const movingAnchor of movingAnchors) {
      const movingValue = getAnchorValue(movingBox, movingAnchor);
      const sectionTargets: Array<{ anchor: SectionAnchor; value: number }> = [
        { anchor: 'sectionTop', value: 0 },
        { anchor: 'sectionCenterY', value: sectionHeight / 2 },
        { anchor: 'sectionBottom', value: sectionHeight },
      ];
      for (const section of sectionTargets) {
        const distancePx = Math.abs(movingValue - section.value);
        if (distancePx > threshold) continue;
        const candidate: AlignmentMatchCandidate = {
          axis: 'y',
          movingAnchor,
          targetAnchor: section.anchor,
          targetValue: section.value,
          movingValue,
          distancePx,
          snapDeltaPx: section.value - movingValue,
        };
        if (!bestSection || candidate.distancePx < bestSection.distancePx) {
          bestSection = candidate;
        }
      }
    }
  }

  if (!bestBlock) return bestSection;
  if (!bestSection) return bestBlock;

  if (Y_PRIORITY_RANK[bestBlockPriority] <= Y_PRIORITY_RANK['nearby-content']) {
    return bestBlock;
  }

  return bestSection.distancePx < bestBlock.distancePx ? bestSection : bestBlock;
}

function buildVerticalAlignmentDebug({
  movingBox,
  neighbors,
  sectionWidth,
  sectionHeight,
  threshold,
  movingBlockId,
  movingBoxSource,
  rowPeerIds,
  explicitRowMode,
  blockKindsById,
  yMatch,
  activeYRendered,
}: {
  movingBox: AlignmentBox;
  neighbors: AlignmentBox[];
  sectionWidth: number;
  sectionHeight: number;
  threshold: number;
  movingBlockId?: string;
  movingBoxSource: AlignmentBoxSource;
  rowPeerIds: Set<string>;
  explicitRowMode: boolean;
  blockKindsById?: Record<string, string>;
  yMatch: AlignmentMatchCandidate | null;
  activeYRendered: boolean;
}): VerticalAlignmentDebug {
  const suppressedYGuides: Array<{ targetBlockId?: string; reason: string }> = [];
  const candidates: VerticalAlignmentCandidateDebug[] = [];

  for (const neighbor of neighbors) {
    if (!neighbor.id) continue;
    const evaluation = evaluateYAlignmentCandidate(
      movingBox,
      neighbor,
      movingBlockId,
      rowPeerIds,
      blockKindsById?.[neighbor.id],
      sectionWidth
    );
    const distancesPx = {
      topToTop: Math.abs(movingBox.top - neighbor.top),
      centerYToCenterY: Math.abs(movingBox.centerY - neighbor.centerY),
      bottomToBottom: Math.abs(movingBox.bottom - neighbor.bottom),
    };
    const withinThreshold =
      distancesPx.topToTop <= threshold ||
      distancesPx.centerYToCenterY <= threshold ||
      distancesPx.bottomToBottom <= threshold;

    if (evaluation.priority === 'excluded') {
      suppressedYGuides.push({
        targetBlockId: neighbor.id,
        reason: evaluation.rejectionReason ?? 'excluded',
      });
    }

    candidates.push({
      blockId: neighbor.id,
      blockKind: blockKindsById?.[neighbor.id],
      chosenBoxSource: 'content',
      activeVisualBox: serializeAlignmentBox(neighbor),
      testedAnchors: ['top', 'centerY', 'bottom'],
      distancesPx,
      acceptedForY: evaluation.priority !== 'excluded' && withinThreshold,
      rejectionReason: evaluation.rejectionReason,
      priority: evaluation.priority,
    });
  }

  let activeYGuide: VerticalAlignmentDebug['activeYGuide'] | undefined;
  if (yMatch) {
    const isSection = isSectionAnchor(yMatch.targetAnchor);
    activeYGuide = {
      movingAnchor: yMatch.movingAnchor,
      targetBlockId: yMatch.targetBlockId,
      targetAnchor: yMatch.targetAnchor,
      distancePx: yMatch.distancePx,
      snapDeltaPx: yMatch.snapDeltaPx,
      priority: isSection ? 'section-anchor' : undefined,
      rendered: activeYRendered,
    };
    if (!isSection && yMatch.targetBox) {
      const evaluation = evaluateYAlignmentCandidate(
        movingBox,
        yMatch.targetBox,
        movingBlockId,
        rowPeerIds,
        blockKindsById?.[yMatch.targetBox.id ?? ''],
        sectionWidth
      );
      if (evaluation.priority !== 'excluded') {
        activeYGuide.priority = evaluation.priority;
      }
    }
  }

  return {
    movingBlockId,
    movingBoxSource,
    movingBox: serializeAlignmentBox(movingBox),
    candidates,
    activeYGuide,
    suppressedYGuides: suppressedYGuides.length > 0 ? suppressedYGuides : undefined,
  };
}

function matchToActiveGuide(
  match: AlignmentMatchCandidate,
  movingBox: AlignmentBox,
  sectionWidth: number,
  sectionHeight: number
): ActiveAlignmentGuide {
  const kind = anchorToGuideKind(match.targetAnchor);
  let spanStart: number;
  let spanEnd: number;

  if (match.axis === 'x') {
    if (isSectionAnchor(match.targetAnchor)) {
      spanStart = 0;
      spanEnd = sectionHeight;
    } else if (match.targetBox) {
      spanStart = Math.min(movingBox.top, match.targetBox.top);
      spanEnd = Math.max(movingBox.bottom, match.targetBox.bottom);
    } else {
      spanStart = movingBox.top;
      spanEnd = movingBox.bottom;
    }
  } else if (isSectionAnchor(match.targetAnchor)) {
    spanStart = 0;
    spanEnd = sectionWidth;
  } else if (match.targetBox) {
    spanStart = Math.min(movingBox.left, match.targetBox.left);
    spanEnd = Math.max(movingBox.right, match.targetBox.right);
  } else {
    spanStart = movingBox.left;
    spanEnd = movingBox.right;
  }

  return {
    axis: match.axis,
    movingAnchor: match.movingAnchor,
    targetBlockId: match.targetBlockId,
    targetAnchor: match.targetAnchor,
    targetValue: match.targetValue,
    movingValue: match.movingValue,
    distancePx: match.distancePx,
    snapDeltaPx: match.snapDeltaPx,
    position: match.targetValue,
    kind,
    spanStart,
    spanEnd,
  };
}

function activeGuideToLine(guide: ActiveAlignmentGuide): AlignmentLineGuide {
  return {
    axis: guide.axis,
    position: guide.position,
    kind: guide.kind,
    spanStart: guide.spanStart,
    spanEnd: guide.spanEnd,
    sourceId: guide.targetBlockId,
  };
}

type GapNeighborContext = {
  leftNeighbor: AlignmentBox | null;
  rightNeighbor: AlignmentBox | null;
  topNeighbor: AlignmentBox | null;
  bottomNeighbor: AlignmentBox | null;
  candidateRejections: CandidateRejection[];
  rowPeerIds: Set<string>;
  explicitRowMode: boolean;
  explicitColumnMode: boolean;
  stackPeerIds: string[];
};

export function resolveGapNeighborContext(
  movingBox: AlignmentBox,
  referenceBoxes: AlignmentBox[],
  sectionWidth: number,
  movingBlockId?: string
): GapNeighborContext {
  const neighbors = referenceBoxes.filter((box) => finiteBox(box) && box.id !== movingBox.id);
  const layoutTier2RowCandidates = partitionRowGapCandidates(
    movingBox,
    neighbors,
    sectionWidth
  ).accepted;
  const layoutTier2ColumnCandidates = partitionColumnGapCandidates(
    movingBox,
    neighbors,
    sectionWidth
  ).accepted;
  const {
    horizontalCandidates,
    verticalCandidates,
    rejections: filterRejections,
    explicitRowMode,
    explicitColumnMode,
    stackPeerIds,
  } = resolveGapPeerContext(movingBox, neighbors, sectionWidth, movingBlockId);

  let leftNeighbor: AlignmentBox | null = null;
  let rightNeighbor: AlignmentBox | null = null;
  let topNeighbor: AlignmentBox | null = null;
  let bottomNeighbor: AlignmentBox | null = null;
  const horizontalRejections: CandidateRejection[] = [];
  const resolvedMovingId = movingBox.id ?? movingBlockId;
  const useStackColumnPeers = isHoldingButtonRowBlock(resolvedMovingId);
  const holdingRowHorizontalOptions = useStackColumnPeers
    ? { requireSameRow: false }
    : undefined;
  leftNeighbor = findImmediateHorizontalNeighbor(
    movingBox,
    horizontalCandidates,
    'left',
    holdingRowHorizontalOptions
  );
  rightNeighbor = findImmediateHorizontalNeighbor(
    movingBox,
    horizontalCandidates,
    'right',
    holdingRowHorizontalOptions
  );
  if (useStackColumnPeers) {
    if (
      leftNeighbor &&
      !isPrimarilyHorizontalNeighbor(movingBox, leftNeighbor, 'left')
    ) {
      leftNeighbor = null;
    }
    if (
      rightNeighbor &&
      !isPrimarilyHorizontalNeighbor(movingBox, rightNeighbor, 'right')
    ) {
      rightNeighbor = null;
    }
    if (explicitColumnMode) {
      if (!leftNeighbor) {
        leftNeighbor = findImmediateHorizontalNeighbor(
          movingBox,
          layoutTier2RowCandidates,
          'left'
        );
        if (
          leftNeighbor &&
          !isPrimarilyHorizontalNeighbor(movingBox, leftNeighbor, 'left')
        ) {
          leftNeighbor = null;
        }
      }
      if (!rightNeighbor) {
        rightNeighbor = findImmediateHorizontalNeighbor(
          movingBox,
          layoutTier2RowCandidates,
          'right'
        );
        if (
          rightNeighbor &&
          !isPrimarilyHorizontalNeighbor(movingBox, rightNeighbor, 'right')
        ) {
          rightNeighbor = null;
        }
      }
    }
  }
  horizontalRejections.push(
    ...collectNonImmediateRejections(
      movingBox,
      horizontalCandidates,
      leftNeighbor,
      rightNeighbor
    )
  );

  const verticalNeighborOptions = useStackColumnPeers
    ? { requireSharesColumn: false, excludeSameRow: true }
    : undefined;
  const verticalSearchCandidates =
    useStackColumnPeers && explicitRowMode
      ? layoutTier2ColumnCandidates
      : verticalCandidates;
  topNeighbor = findImmediateVerticalNeighbor(
    movingBox,
    verticalSearchCandidates,
    'top',
    verticalNeighborOptions
  );
  bottomNeighbor = findImmediateVerticalNeighbor(
    movingBox,
    verticalSearchCandidates,
    'bottom',
    verticalNeighborOptions
  );
  if (useStackColumnPeers) {
    if (
      topNeighbor &&
      !isPrimarilyVerticalNeighbor(movingBox, topNeighbor, 'top')
    ) {
      topNeighbor = null;
    }
    if (
      bottomNeighbor &&
      !isPrimarilyVerticalNeighbor(movingBox, bottomNeighbor, 'bottom')
    ) {
      bottomNeighbor = null;
    }
  }
  if (!useStackColumnPeers && !topNeighbor && !bottomNeighbor) {
    topNeighbor = findNearestVerticalNeighbor(movingBox, verticalCandidates, 'top');
    bottomNeighbor = findNearestVerticalNeighbor(movingBox, verticalCandidates, 'bottom');
  }

  const verticalRejections = collectVerticalNonImmediateRejections(
    verticalSearchCandidates,
    topNeighbor,
    bottomNeighbor
  );
  const candidateRejections = [
    ...filterRejections,
    ...horizontalRejections,
    ...verticalRejections,
  ];
  const rowPeerIds = new Set(
    (explicitColumnMode || explicitRowMode ? stackPeerIds : horizontalCandidates.map((box) => box.id))
      .filter((id): id is string => Boolean(id))
  );

  return {
    leftNeighbor,
    rightNeighbor,
    topNeighbor,
    bottomNeighbor,
    candidateRejections,
    rowPeerIds,
    explicitRowMode,
    explicitColumnMode,
    stackPeerIds,
  };
}

export function computeEqualSpacingSnapTargets(
  movingBox: AlignmentBox,
  referenceBoxes: AlignmentBox[],
  sectionWidth = 0,
  movingBlockId?: string
): {
  deltaX: number;
  deltaY: number;
  distanceX: number;
  distanceY: number;
} {
  if (!finiteBox(movingBox)) {
    return { deltaX: 0, deltaY: 0, distanceX: Infinity, distanceY: Infinity };
  }

  const {
    leftNeighbor,
    rightNeighbor,
    topNeighbor,
    bottomNeighbor,
  } = resolveGapNeighborContext(movingBox, referenceBoxes, sectionWidth, movingBlockId);

  let deltaX = 0;
  let distanceX = Infinity;
  if (leftNeighbor && rightNeighbor) {
    const idealLeft = (rightNeighbor.left + leftNeighbor.right - movingBox.width) / 2;
    deltaX = idealLeft - movingBox.left;
    distanceX = Math.abs(deltaX);
  }

  let deltaY = 0;
  let distanceY = Infinity;
  if (topNeighbor && bottomNeighbor) {
    const idealTop = (bottomNeighbor.top + topNeighbor.bottom - movingBox.height) / 2;
    deltaY = idealTop - movingBox.top;
    distanceY = Math.abs(deltaY);
  }

  return { deltaX, deltaY, distanceX, distanceY };
}

export function collectLayoutSpacingRhythm(
  referenceBoxes: AlignmentBox[],
  sectionWidth = 0,
  excludeBlockId?: string,
  options?: { rowHorizontalGapsOnly?: boolean }
): number[] {
  const gaps = new Set<number>();
  const rowHorizontalOnly = options?.rowHorizontalGapsOnly ?? false;

  for (const box of referenceBoxes) {
    if (!finiteBox(box) || !box.id || box.id === excludeBlockId) continue;
    const context = resolveGapNeighborContext(box, referenceBoxes, sectionWidth, box.id);

    if (context.leftNeighbor) {
      if (!rowHorizontalOnly || isSameRow(box, context.leftNeighbor)) {
        const gapPx = Math.round(box.left - context.leftNeighbor.right);
        if (gapPx > GAP_TOUCH_TOLERANCE_PX) gaps.add(gapPx);
      }
    }
    if (context.rightNeighbor) {
      if (!rowHorizontalOnly || isSameRow(box, context.rightNeighbor)) {
        const gapPx = Math.round(context.rightNeighbor.left - box.right);
        if (gapPx > GAP_TOUCH_TOLERANCE_PX) gaps.add(gapPx);
      }
    }
    if (!rowHorizontalOnly) {
      if (context.topNeighbor) {
        const gapPx = Math.round(box.top - context.topNeighbor.bottom);
        if (gapPx > GAP_TOUCH_TOLERANCE_PX) gaps.add(gapPx);
      }
      if (context.bottomNeighbor) {
        const gapPx = Math.round(context.bottomNeighbor.top - box.bottom);
        if (gapPx > GAP_TOUCH_TOLERANCE_PX) gaps.add(gapPx);
      }
    }
  }

  return Array.from(gaps).sort((left, right) => left - right);
}

export function computeRhythmGapSnapTargets(
  movingBox: AlignmentBox,
  referenceBoxes: AlignmentBox[],
  sectionWidth = 0,
  movingBlockId?: string
): {
  deltaX: number;
  deltaY: number;
  distanceX: number;
  distanceY: number;
  matchedGapX?: number;
  matchedGapY?: number;
  rhythmGaps: number[];
} {
  const empty = {
    deltaX: 0,
    deltaY: 0,
    distanceX: Infinity,
    distanceY: Infinity,
    rhythmGaps: [] as number[],
  };

  if (!finiteBox(movingBox)) {
    return empty;
  }

  const resolvedMovingId = movingBlockId ?? movingBox.id;
  const rhythmGaps = collectLayoutSpacingRhythm(
    referenceBoxes,
    sectionWidth,
    resolvedMovingId,
    resolveLayoutRhythmGapOptions(resolvedMovingId)
  );
  if (rhythmGaps.length === 0) {
    return empty;
  }

  const {
    leftNeighbor,
    rightNeighbor,
    topNeighbor,
    bottomNeighbor,
  } = resolveGapNeighborContext(movingBox, referenceBoxes, sectionWidth, movingBlockId);

  let bestX = { delta: 0, distance: Infinity, gap: undefined as number | undefined };
  let bestY = { delta: 0, distance: Infinity, gap: undefined as number | undefined };

  const considerX = (delta: number, gapPx: number) => {
    const distance = Math.abs(delta);
    if (distance < bestX.distance) {
      bestX = { delta, distance, gap: gapPx };
    }
  };

  const considerY = (delta: number, gapPx: number) => {
    const distance = Math.abs(delta);
    if (distance < bestY.distance) {
      bestY = { delta, distance, gap: gapPx };
    }
  };

  for (const gapPx of rhythmGaps) {
    if (leftNeighbor) {
      considerX(leftNeighbor.right + gapPx - movingBox.left, gapPx);
    }
    if (rightNeighbor) {
      considerX(rightNeighbor.left - gapPx - movingBox.width - movingBox.left, gapPx);
    }
    if (leftNeighbor && rightNeighbor) {
      const idealLeft = leftNeighbor.right + gapPx;
      const rightGap = Math.round(rightNeighbor.left - (idealLeft + movingBox.width));
      if (rightGap === gapPx) {
        considerX(idealLeft - movingBox.left, gapPx);
      }
    }

    if (topNeighbor) {
      considerY(topNeighbor.bottom + gapPx - movingBox.top, gapPx);
    }
    if (bottomNeighbor) {
      considerY(bottomNeighbor.top - gapPx - movingBox.height - movingBox.top, gapPx);
    }
    if (topNeighbor && bottomNeighbor) {
      const idealTop = topNeighbor.bottom + gapPx;
      const bottomGap = Math.round(bottomNeighbor.top - (idealTop + movingBox.height));
      if (bottomGap === gapPx) {
        considerY(idealTop - movingBox.top, gapPx);
      }
    }
  }

  const peers = referenceBoxes.filter(
    (box) => finiteBox(box) && box.id !== resolvedMovingId
  );
  const rowPeers = peers.filter((peer) => isSameRow(movingBox, peer));
  if (rowPeers.length > 0) {
    const rightmost = rowPeers.reduce((best, peer) =>
      peer.right > best.right ? peer : best
    );
    for (const gapPx of rhythmGaps) {
      considerX(rightmost.right + gapPx - movingBox.left, gapPx);
    }
  }

  const columnPeers = peers.filter((peer) => sharesColumn(movingBox, peer));
  if (columnPeers.length > 0) {
    const bottommost = columnPeers.reduce((best, peer) =>
      peer.bottom > best.bottom ? peer : best
    );
    for (const gapPx of rhythmGaps) {
      considerY(bottommost.bottom + gapPx - movingBox.top, gapPx);
    }
  }

  return {
    deltaX: bestX.distance === Infinity ? 0 : bestX.delta,
    deltaY: bestY.distance === Infinity ? 0 : bestY.delta,
    distanceX: bestX.distance,
    distanceY: bestY.distance,
    matchedGapX: bestX.gap,
    matchedGapY: bestY.gap,
    rhythmGaps,
  };
}

export function calculateGapGuides(
  movingBox: AlignmentBox,
  referenceBoxes: AlignmentBox[],
  sectionWidth = 0,
  movingBlockId?: string,
  movingBoxSource: AlignmentBoxSource = 'content'
): {
  guides: GapGuide[];
  gapDebug: GapGuideDebug[];
  equal: boolean;
  equalX: boolean;
  equalY: boolean;
  equalAll: boolean;
  candidateRejections: CandidateRejection[];
  rowPeerIds: Set<string>;
  explicitRowMode: boolean;
  explicitColumnMode: boolean;
  stackPeerIds: string[];
  verticalSpacingDebug?: VerticalSpacingDebug;
} {
  if (!finiteBox(movingBox)) {
    return {
      guides: [],
      gapDebug: [],
      equal: false,
      equalX: false,
      equalY: false,
      equalAll: false,
      candidateRejections: [],
      rowPeerIds: new Set(),
      explicitRowMode: false,
      explicitColumnMode: false,
      stackPeerIds: [],
    };
  }

  const {
    leftNeighbor,
    rightNeighbor,
    topNeighbor,
    bottomNeighbor,
    candidateRejections,
    rowPeerIds,
    explicitRowMode,
    explicitColumnMode,
    stackPeerIds,
  } = resolveGapNeighborContext(movingBox, referenceBoxes, sectionWidth, movingBlockId);

  const guides: GapGuide[] = [];
  const gapDebug: GapGuideDebug[] = [];

  const leftComputedGap = leftNeighbor ? movingBox.left - leftNeighbor.right : null;
  const rightComputedGap = rightNeighbor ? rightNeighbor.left - movingBox.right : null;

  if (
    leftNeighbor &&
    leftComputedGap !== null &&
    shouldShowRowGapGuide(leftComputedGap, rightComputedGap)
  ) {
    const computedGap = leftComputedGap;
    const display = resolveGapDisplay(computedGap);
    const geometry = computeHorizontalGapGeometry(movingBox, leftNeighbor, 'left');
    const debug = buildHorizontalGapDebug(
      'left',
      movingBox,
      leftNeighbor,
      computedGap,
      display.label
    );
    gapDebug.push(debug);
    guides.push({
      axis: 'x',
      side: 'left',
      neighbourBlockId: leftNeighbor.id,
      gapPx: display.gapPx,
      start: geometry.start,
      end: geometry.end,
      labelPosition: geometry.labelPosition,
      overlap: display.overlap,
      touch: display.touch,
      displayLabel: display.label,
      guideLineY: geometry.guideLineY,
      edgeMarkerStart: geometry.edgeMarkerStart,
      edgeMarkerEnd: geometry.edgeMarkerEnd,
      involvesMovingBox: true,
      debug,
    });
  }

  if (
    rightNeighbor &&
    rightComputedGap !== null &&
    shouldShowRowGapGuide(rightComputedGap, leftComputedGap)
  ) {
    const computedGap = rightComputedGap;
    const display = resolveGapDisplay(computedGap);
    const geometry = computeHorizontalGapGeometry(movingBox, rightNeighbor, 'right');
    const debug = buildHorizontalGapDebug(
      'right',
      movingBox,
      rightNeighbor,
      computedGap,
      display.label
    );
    gapDebug.push(debug);
    guides.push({
      axis: 'x',
      side: 'right',
      neighbourBlockId: rightNeighbor.id,
      gapPx: display.gapPx,
      start: geometry.start,
      end: geometry.end,
      labelPosition: geometry.labelPosition,
      overlap: display.overlap,
      touch: display.touch,
      displayLabel: display.label,
      guideLineY: geometry.guideLineY,
      edgeMarkerStart: geometry.edgeMarkerStart,
      edgeMarkerEnd: geometry.edgeMarkerEnd,
      involvesMovingBox: true,
      debug,
    });
  }

  const topComputedGap = topNeighbor ? movingBox.top - topNeighbor.bottom : null;
  const bottomComputedGap = bottomNeighbor ? bottomNeighbor.top - movingBox.bottom : null;

  if (
    topNeighbor &&
    topComputedGap !== null &&
    shouldShowRowGapGuide(topComputedGap, bottomComputedGap)
  ) {
    const computedGap = topComputedGap;
    const display = resolveGapDisplay(computedGap);
    const geometry = computeVerticalGapGeometry(movingBox, topNeighbor, 'top');
    const debug = buildVerticalGapDebug(
      'top',
      movingBox,
      topNeighbor,
      computedGap,
      display.label
    );
    gapDebug.push(debug);
    guides.push({
      axis: 'y',
      side: 'top',
      neighbourBlockId: topNeighbor.id,
      gapPx: display.gapPx,
      start: geometry.start,
      end: geometry.end,
      labelPosition: geometry.labelPosition,
      overlap: display.overlap,
      touch: display.touch,
      displayLabel: display.label,
      guideLineX: geometry.guideLineX,
      edgeMarkerStart: geometry.edgeMarkerStart,
      edgeMarkerEnd: geometry.edgeMarkerEnd,
      involvesMovingBox: true,
      debug,
    });
  }

  if (
    bottomNeighbor &&
    bottomComputedGap !== null &&
    shouldShowRowGapGuide(bottomComputedGap, topComputedGap)
  ) {
    const computedGap = bottomComputedGap;
    const display = resolveGapDisplay(computedGap);
    const geometry = computeVerticalGapGeometry(movingBox, bottomNeighbor, 'bottom');
    const debug = buildVerticalGapDebug(
      'bottom',
      movingBox,
      bottomNeighbor,
      computedGap,
      display.label
    );
    gapDebug.push(debug);
    guides.push({
      axis: 'y',
      side: 'bottom',
      neighbourBlockId: bottomNeighbor.id,
      gapPx: display.gapPx,
      start: geometry.start,
      end: geometry.end,
      labelPosition: geometry.labelPosition,
      overlap: display.overlap,
      touch: display.touch,
      displayLabel: display.label,
      guideLineX: geometry.guideLineX,
      edgeMarkerStart: geometry.edgeMarkerStart,
      edgeMarkerEnd: geometry.edgeMarkerEnd,
      involvesMovingBox: true,
      debug,
    });
  }

  mergeGapGuides(guides, calculateRowColumnDistributionGapGuides(movingBox, referenceBoxes));

  let equal = false;
  let equalX = false;
  let equalY = false;
  let equalAll = false;
  let equalSpacingRendered = false;

  function gapsAreEqualPair(leftGuide: GapGuide, rightGuide: GapGuide) {
    if (leftGuide.overlap || rightGuide.overlap) return false;
    const leftValue = leftGuide.gapPx;
    const rightValue = rightGuide.gapPx;
    if (leftValue < 0 || rightValue < 0) return false;
    if (
      leftValue <= GAP_TOUCH_TOLERANCE_PX ||
      rightValue <= GAP_TOUCH_TOLERANCE_PX
    ) {
      return false;
    }
    return leftValue === rightValue;
  }

  const leftGap = guides.find((guide) => guide.side === 'left');
  const rightGap = guides.find((guide) => guide.side === 'right');
  const topGap = guides.find((guide) => guide.side === 'top');
  const bottomGap = guides.find((guide) => guide.side === 'bottom');
  const cardinalGaps = [leftGap, rightGap, topGap, bottomGap].filter(
    (guide): guide is GapGuide => Boolean(guide)
  );

  const horizontalPairEqual =
    Boolean(leftGap && rightGap && gapsAreEqualPair(leftGap, rightGap));
  const verticalPairEqual =
    Boolean(topGap && bottomGap && gapsAreEqualPair(topGap, bottomGap));
  const horizontalEqualValue = horizontalPairEqual ? leftGap!.gapPx : null;
  const verticalEqualValue = verticalPairEqual ? topGap!.gapPx : null;

  if (cardinalGaps.length === 4) {
    const values = cardinalGaps.map((guide) => guide.gapPx);
    const allPositive = values.every((value) => value > GAP_TOUCH_TOLERANCE_PX);
    const noOverlap = cardinalGaps.every((guide) => !guide.overlap);
    const uniformDisplayGap = values.every((value) => value === values[0]);
    if (allPositive && noOverlap && uniformDisplayGap) {
      cardinalGaps.forEach((guide) => {
        guide.equalGroupId = 'equal-all';
      });
      equal = true;
      equalX = true;
      equalY = true;
      equalAll = true;
      equalSpacingRendered = true;
    }
  }

  if (!equalAll) {
    if (horizontalPairEqual) {
      leftGap!.equalGroupId = 'equal-x';
      rightGap!.equalGroupId = 'equal-x';
      equal = true;
      equalX = true;
      equalSpacingRendered = true;
    }

    if (verticalPairEqual) {
      const verticalMatchesHorizontal =
        horizontalEqualValue !== null && verticalEqualValue === horizontalEqualValue;
      const verticalGroupId = verticalMatchesHorizontal ? 'equal-y' : 'equal-y-axis';
      topGap!.equalGroupId = verticalGroupId;
      bottomGap!.equalGroupId = verticalGroupId;
      equal = true;
      equalY = true;
      equalSpacingRendered = true;
    }
  }

  applyRowColumnChainEqualSpacing(guides);
  if (guides.some((guide) => guide.equalGroupId === 'equal-x')) {
    equal = true;
    equalX = true;
    equalSpacingRendered = true;
  }
  if (guides.some((guide) => guide.equalGroupId === 'equal-y' || guide.equalGroupId === 'equal-y-axis')) {
    equal = true;
    equalY = true;
    equalSpacingRendered = true;
  }
  if (guides.some((guide) => guide.equalGroupId === 'equal-all')) {
    equal = true;
    equalX = true;
    equalY = true;
    equalAll = true;
    equalSpacingRendered = true;
  }

  const layoutRhythmGaps = collectLayoutSpacingRhythm(
    referenceBoxes,
    sectionWidth,
    movingBox.id ?? movingBlockId,
    resolveLayoutRhythmGapOptions(movingBox.id ?? movingBlockId, explicitRowMode)
  );
  if (layoutRhythmGaps.length > 0) {
    for (const guide of guides) {
      if (!guide.involvesMovingBox || guide.overlap || guide.equalGroupId || guide.touch) continue;
      if (layoutRhythmGaps.includes(guide.gapPx)) {
        guide.rhythmMatch = true;
      }
    }
  }

  const topDisplay =
    topNeighbor && topComputedGap !== null ? resolveGapDisplay(topComputedGap) : null;
  const bottomDisplay =
    bottomNeighbor && bottomComputedGap !== null ? resolveGapDisplay(bottomComputedGap) : null;
  const verticalSpacingDebug = buildVerticalSpacingDebug({
    movingBlockId: movingBox.id ?? movingBlockId,
    movingBox,
    movingBoxSource,
    stackPeerIds,
    topNeighbor,
    bottomNeighbor,
    topComputedGap,
    bottomComputedGap,
    topDisplay,
    bottomDisplay,
    equalSpacingRendered,
    rejectedCandidates: candidateRejections,
  });

  return {
    guides,
    gapDebug,
    equal,
    equalX,
    equalY,
    equalAll,
    candidateRejections,
    rowPeerIds,
    explicitRowMode,
    explicitColumnMode,
    stackPeerIds,
    verticalSpacingDebug,
  };
}

function gapGuideToSpacingGuide(gap: GapGuide, movingBox: AlignmentBox): SpacingGuide {
  const axisPosition =
    gap.axis === 'x'
      ? gap.guideLineY ?? movingBox.centerY
      : gap.guideLineX ?? movingBox.centerX;
  const edgeMarkerStart =
    gap.edgeMarkerStart ?? (gap.axis === 'x' ? movingBox.top : movingBox.left);
  const edgeMarkerEnd =
    gap.edgeMarkerEnd ?? (gap.axis === 'x' ? movingBox.bottom : movingBox.right);

  return {
    axis: gap.axis,
    start: gap.start,
    end: gap.end,
    position: axisPosition,
    gap: gap.gapPx,
    sourceIds: gap.neighbourBlockId ? [gap.neighbourBlockId] : [],
    equalGroupId: gap.equalGroupId,
    rhythmMatch: gap.rhythmMatch,
    label: gap.displayLabel ?? gap.debug?.displayedLabel ?? '0',
    placement: gap.side === 'left' || gap.side === 'top' ? 'leading' : 'trailing',
    edgeMarkerStart,
    edgeMarkerEnd,
    labelPosition: gap.labelPosition,
    overlap: gap.overlap,
    touch: gap.touch,
  };
}

export function computeAlignmentAnalysis({
  movingBox,
  referenceBoxes,
  sectionWidth,
  sectionHeight,
  threshold,
  movingBlockId,
  movingBoxSource = 'layout',
  blockKindsById,
}: {
  movingBox: AlignmentBox;
  referenceBoxes: AlignmentBox[];
  sectionWidth: number;
  sectionHeight: number;
  threshold: number;
  movingBlockId?: string;
  movingBoxSource?: AlignmentBoxSource;
  blockKindsById?: Record<string, string>;
}): AlignmentAnalysis {
  const resolvedMovingId = movingBox.id ?? movingBlockId;
  const neighbors = referenceBoxes.filter(
    (box) => finiteBox(box) && box.id !== resolvedMovingId
  );
  const {
    guides: gapGuides,
    gapDebug,
    equal,
    equalX,
    equalY,
    equalAll,
    candidateRejections,
    rowPeerIds,
    explicitRowMode,
    explicitColumnMode,
    verticalSpacingDebug,
  } = calculateGapGuides(
    movingBox,
    referenceBoxes,
    sectionWidth,
    movingBlockId,
    movingBoxSource
  );
  const xMatch = findBestXAlignmentMatch(
    movingBox,
    neighbors,
    sectionWidth,
    sectionHeight,
    threshold,
    rowPeerIds,
    explicitRowMode
  );
  const yMatch = findBestYAlignmentMatch(movingBox, neighbors, sectionWidth, sectionHeight, threshold, {
    movingBlockId: resolvedMovingId,
    rowPeerIds,
    explicitRowMode,
    explicitColumnMode,
    blockKindsById,
  });
  const activeXGuide = xMatch
    ? matchToActiveGuide(xMatch, movingBox, sectionWidth, sectionHeight)
    : undefined;
  const activeYGuide = yMatch
    ? matchToActiveGuide(yMatch, movingBox, sectionWidth, sectionHeight)
    : undefined;
  const verticalAlignmentDebug = buildVerticalAlignmentDebug({
    movingBox,
    neighbors,
    sectionWidth,
    sectionHeight,
    threshold,
    movingBlockId: resolvedMovingId,
    movingBoxSource,
    rowPeerIds,
    explicitRowMode,
    blockKindsById,
    yMatch,
    activeYRendered: Boolean(activeYGuide),
  });

  return {
    movingBox,
    candidateCount: neighbors.length,
    candidateRejections,
    rowPeerIds: Array.from(rowPeerIds),
    explicitRowMode,
    explicitColumnMode,
    activeXGuide,
    activeYGuide,
    verticalAlignmentDebug,
    verticalSpacingDebug,
    gapGuides,
    gapDebug,
    spacingEqual: equal,
    spacingEqualX: equalX,
    spacingEqualY: equalY,
    spacingEqualAll: equalAll,
    boxSource: movingBoxSource,
    coordinateSpace: 'section-layer-px',
  };
}

export function guidesFromAnalysis(
  analysis: AlignmentAnalysis,
  displayBox: AlignmentBox
): AlignmentGuides {
  const lines: AlignmentLineGuide[] = [];
  if (analysis.activeXGuide) {
    lines.push(activeGuideToLine(analysis.activeXGuide));
  }
  if (analysis.activeYGuide) {
    lines.push(activeGuideToLine(analysis.activeYGuide));
  }

  const spacing = analysis.gapGuides.map((gap) => gapGuideToSpacingGuide(gap, displayBox));

  return {
    lines,
    spacing,
    spacingEqual: analysis.spacingEqual,
    spacingEqualX: analysis.spacingEqualX,
    spacingEqualY: analysis.spacingEqualY,
    spacingEqualAll: analysis.spacingEqualAll,
    activeX: analysis.activeXGuide ?? null,
    activeY: analysis.activeYGuide ?? null,
    gapGuides: analysis.gapGuides,
    x: null,
    y: null,
  };
}

export function calculateSpacingGuides({
  movingBox,
  referenceBoxes,
}: {
  movingBox: AlignmentBox;
  referenceBoxes: AlignmentBox[];
}): {
  guides: SpacingGuide[];
  equal: boolean;
} {
  const { guides, equal } = calculateGapGuides(movingBox, referenceBoxes);
  return {
    guides: guides.map((gap) => gapGuideToSpacingGuide(gap, movingBox)),
    equal,
  };
}

export function calculateMoveAlignmentSnap({
  movingBox,
  referenceBoxes,
  sectionWidth,
  sectionHeight,
  threshold,
}: {
  movingBox: AlignmentBox;
  referenceBoxes: AlignmentBox[];
  sectionWidth: number;
  sectionHeight: number;
  threshold: number;
}): {
  offsetXDelta: number;
  offsetYDelta: number;
  guides: AlignmentGuides;
} {
  const analysis = computeAlignmentAnalysis({
    movingBox,
    referenceBoxes,
    sectionWidth,
    sectionHeight,
    threshold,
  });

  return {
    offsetXDelta: analysis.activeXGuide?.snapDeltaPx ?? 0,
    offsetYDelta: analysis.activeYGuide?.snapDeltaPx ?? 0,
    guides: {
      x: analysis.activeXGuide
        ? {
            axis: 'x',
            kind: analysis.activeXGuide.kind,
            position: analysis.activeXGuide.position,
            sourceId: analysis.activeXGuide.targetBlockId,
          }
        : null,
      y: analysis.activeYGuide
        ? {
            axis: 'y',
            kind: analysis.activeYGuide.kind,
            position: analysis.activeYGuide.position,
            sourceId: analysis.activeYGuide.targetBlockId,
          }
        : null,
    },
  };
}

export function resolveAlignmentGuidePosition(guide: AlignmentGuides['x']): number | null {
  if (typeof guide === 'number') return guide;
  if (guide && typeof guide.position === 'number') return guide.position;
  return null;
}

export function serializeAlignmentBox(box: AlignmentBox) {
  return {
    left: box.left,
    right: box.right,
    top: box.top,
    bottom: box.bottom,
    centerX: box.centerX,
    centerY: box.centerY,
    width: box.width,
    height: box.height,
  };
}

export function formatActiveGuideForDebug(guide: ActiveAlignmentGuide | undefined) {
  if (!guide) return undefined;
  return {
    movingAnchor: guide.movingAnchor,
    targetBlockId: guide.targetBlockId,
    targetAnchor: guide.targetAnchor,
    targetValue: guide.targetValue,
    movingValue: guide.movingValue,
    distancePx: guide.distancePx,
    snapDeltaPx: guide.snapDeltaPx,
  };
}

export function formatAlignmentDebugPayload(
  analysis: AlignmentAnalysis,
  snapApplied?: boolean
) {
  return {
    movingBox: serializeAlignmentBox(analysis.movingBox),
    candidateCount: analysis.candidateCount,
    activeXGuide: formatActiveGuideForDebug(analysis.activeXGuide),
    activeYGuide: formatActiveGuideForDebug(analysis.activeYGuide),
    gapGuides: analysis.gapGuides.map((gap) => ({
      axis: gap.axis,
      side: gap.side,
      neighbourBlockId: gap.neighbourBlockId,
      gapPx: gap.gapPx,
      overlap: gap.overlap ?? false,
      displayLabel: gap.displayLabel ?? gap.debug?.displayedLabel,
      labelPosition: gap.labelPosition,
    })),
    gapDebug: analysis.gapDebug,
    candidateRejections: analysis.candidateRejections,
    rowPeerIds: analysis.rowPeerIds,
    explicitRowMode: analysis.explicitRowMode,
    explicitColumnMode: analysis.explicitColumnMode,
    boxSource: analysis.boxSource,
    coordinateSpace: analysis.coordinateSpace,
    snapApplied,
    verticalAlignmentDebug: analysis.verticalAlignmentDebug,
    verticalSpacingDebug: analysis.verticalSpacingDebug,
  };
}

export function buildDragAlignmentGuides({
  movingBox,
  referenceBoxes,
  sectionWidth,
  sectionHeight,
  threshold,
  applySnapToPosition,
  movingBlockId,
  movingBoxSource = 'layout',
  blockKindsById,
  magneticDisplayBox,
}: {
  movingBox: AlignmentBox;
  referenceBoxes: AlignmentBox[];
  sectionWidth: number;
  sectionHeight: number;
  threshold: number;
  applySnapToPosition: boolean;
  movingBlockId?: string;
  movingBoxSource?: AlignmentBoxSource;
  blockKindsById?: Record<string, string>;
  /** When set, gap guides render at this magnetised preview position. */
  magneticDisplayBox?: AlignmentBox;
}): {
  offsetXDelta: number;
  offsetYDelta: number;
  guides: AlignmentGuides;
  movingBox: AlignmentBox;
  analysis: AlignmentAnalysis;
} {
  const previewAnalysis = computeAlignmentAnalysis({
    movingBox,
    referenceBoxes,
    sectionWidth,
    sectionHeight,
    threshold,
    movingBlockId,
    movingBoxSource,
    blockKindsById,
  });
  const offsetXDelta = applySnapToPosition ? previewAnalysis.activeXGuide?.snapDeltaPx ?? 0 : 0;
  const offsetYDelta = applySnapToPosition ? previewAnalysis.activeYGuide?.snapDeltaPx ?? 0 : 0;
  const alignmentSnappedBox = applySnapToPosition
    ? translateAlignmentBox(movingBox, offsetXDelta, offsetYDelta)
    : movingBox;
  const displayBox = magneticDisplayBox ?? alignmentSnappedBox;
  const gapResult = calculateGapGuides(
    displayBox,
    referenceBoxes,
    sectionWidth,
    movingBlockId ?? displayBox.id,
    movingBoxSource
  );
  const analysis: AlignmentAnalysis = {
    ...previewAnalysis,
    movingBox: displayBox,
    gapGuides: gapResult.guides,
    gapDebug: gapResult.gapDebug,
    spacingEqual: gapResult.equal,
    spacingEqualX: gapResult.equalX,
    spacingEqualY: gapResult.equalY,
    spacingEqualAll: gapResult.equalAll,
    candidateRejections: gapResult.candidateRejections,
    rowPeerIds: Array.from(gapResult.rowPeerIds),
    explicitRowMode: gapResult.explicitRowMode,
    explicitColumnMode: gapResult.explicitColumnMode,
    verticalSpacingDebug: gapResult.verticalSpacingDebug,
  };

  return {
    offsetXDelta,
    offsetYDelta,
    guides: guidesFromAnalysis(analysis, displayBox),
    movingBox,
    analysis,
  };
}
